<template>
  <div class="flex items-center h-full w-full relative">
    <span :class="cellClasses">
      <template v-if="isLoading && showLoadingIndicator">
        <Icon 
          name="line-md:loading-loop" 
          class="w-4 h-4 text-muted-foreground mr-1" 
        />
        <span class="text-muted-foreground">Loading...</span>
      </template>
      <template v-else>
        {{ displayValue }}
      </template>
    </span>
    <div v-if="hasApiData" class="absolute top-0.5 right-0.5 text-primary/70 hover:text-primary text-xs pointer-events-none">
      🛈
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  params: any
}

const props = defineProps<Props>()

// Add error handling for params access
const safeParams = computed(() => props.params || {})

// Check if loading indicators should be shown for this column
const showLoadingIndicator = computed(() => {
  try {
    const colDef = safeParams.value.colDef
    // Check if the column definition has showLoadingIndicator set to true
    return colDef?.showLoadingIndicator === true
  } catch (error) {
    console.warn('CommonNameCell showLoadingIndicator error:', error)
    return false
  }
})

// Check if this cell is currently loading data
const isLoading = computed(() => {
  try {
    const gridContext = safeParams.value.context
    const field = safeParams.value.colDef?.field
    const rowIndex = safeParams.value.node?.rowIndex
    
    // Check if this specific cell is loading from autocomplete or transformers
    if (gridContext?.autocompleteLoadingCells && field && rowIndex !== undefined) {
      const cellId = `${rowIndex}-${field}`
      return gridContext.autocompleteLoadingCells.has(cellId)
    }
    
    // Also check if the value indicates it's loading/pending
    const value = safeParams.value.value
    if (typeof value === 'object' && value !== null) {
      return value.loading === true || value.status === 'loading' || value.status === 'pending'
    }
    
    return false
  } catch (error) {
    console.warn('CommonNameCell isLoading error:', error)
    return false
  }
})

const displayValue = computed(() => {
  try {
    const value = safeParams.value.value
    if (value === null || value === undefined) return ''
    
    if (typeof value === 'object' && value !== null) {
      return value.label || value.value || ''
    }
    
    return value
  } catch (error) {
    console.warn('CommonNameCell displayValue error:', error)
    return ''
  }
})

const hasApiData = computed(() => {
  try {
    const value = safeParams.value.value
    return typeof value === 'object' && value !== null && 
      (value.sourceName || value.groundingMetadata || value.sourceText)
  } catch (error) {
    console.warn('CommonNameCell hasApiData error:', error)
    return false
  }
})

const cellClasses = computed(() => {
  const baseClasses = 'italic text-sm leading-normal flex items-center'
  
  if (hasApiData.value) {
    return `${baseClasses} border-l-2 border-primary/30 pl-2`
  }
  
  return baseClasses
})
</script> 